import "./context-menu.css"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { useTranslation } from "@nova/use-translation"
import { SmallIcon } from "@nova/component-icons"
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { navbarHeight, sidePanelMinWidth } from "@nova/constants"

type ContextMenuProps = {
    mousePosition: { top: number; left: number }
    setMousePosition: (position?: { top: number; left: number }) => void
    menuOptions: {
        label: string
        onClick: () => void
        Icon?: React.FunctionComponent<React.SVGAttributes<SVGElement>>
        style?: React.CSSProperties
    }[]
}
const offset = { x: 14, y: 24 }
export const ContextMenu = ({ mousePosition, setMousePosition, menuOptions }: ContextMenuProps) => {
    const { t } = useTranslation()
    const menuRef = useRef<HTMLDivElement>(null)
    const [constraints, setConstraints] = useState({ goesOffRight: false, goesOffBottom: false })
    useLayoutEffect(() => {
        const width = (menuRef.current?.clientWidth || 0) + (menuRef.current?.scrollWidth || 0)
        const height = (menuRef.current?.clientHeight || 0) + (menuRef.current?.scrollHeight || 0)
        const goesOffRight = mousePosition.left + width + sidePanelMinWidth > window.innerWidth
        const goesOffBottom = mousePosition.top + height + navbarHeight > window.innerHeight
        setConstraints({ goesOffRight, goesOffBottom })
    }, [mousePosition.left, mousePosition.top])
    const top = useMemo(() => {
        if (constraints.goesOffBottom) {
            return mousePosition.top - offset.y
        }
        return mousePosition.top + offset.y
    }, [constraints.goesOffBottom, mousePosition.top])
    const bottom = useMemo(() => {
        if (constraints.goesOffRight) {
            return mousePosition.left - offset.x
        }
        return mousePosition.left + offset.x
    }, [constraints.goesOffRight, mousePosition.left])
    useEffect(() => {
        if (typeof window === "undefined") return
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setMousePosition(undefined)
            }
        }
        window.addEventListener("keydown", handler)
        return () => window.removeEventListener("keydown", handler)
    })
    return (
        <ClickAwayListener onClickAway={() => setMousePosition(undefined)}>
            <div
                ref={menuRef}
                className="context-menu"
                style={{
                    top: `${top}px`,
                    left: `${bottom}px`,
                    translate: `${constraints.goesOffRight ? "-100%" : "0"} ${constraints.goesOffBottom ? "-100%" : "0"}`,
                }}
                data-testid="context-menu"
            >
                <ul className="context-menu__list">
                    {menuOptions.map((option, i) => (
                        <li
                            key={i}
                            className="context-menu__list-button flex items-center gap-2 justify-between"
                            onClick={option.onClick}
                            style={option.style}
                        >
                            <span style={{ wordBreak: "keep-all" }}>{t(option.label)}</span>
                            {option.Icon && <SmallIcon Icon={option.Icon} />}
                        </li>
                    ))}
                </ul>
            </div>
        </ClickAwayListener>
    )
}
